/** The coordinates of a tile in the canvas */
type TileCoordinate = {
  /** The x coordinate of the pixel of the tile in the canvas */
  x: number;
  /** The y coordinate of the pixel of the tile in the canvas */
  y: number;
};

/**
 * Iterator for generating tile coordinates from a canvas.
 */
export class GenerateTilesIterator {
  /** Stores the value of the next tile coordinates when hasNext() is called. */
  private hasNextIteratorResult: IteratorResult<TileCoordinate> | null;

  /** Stores the generator for tile coordinates. */
  private generator: Generator<TileCoordinate>;

  /**
   * Creates an instance of GenerateTilesIterator.
   *
   * @param canvasSize - The size of the canvas (width and height).
   * @param tilesSize - The size of each tile in pixels.
   */
  constructor(
    private canvasSize: { width: number; height: number },
    private tilesSize: number,
  ) {
    this.generator = this.createGenerator();
    this.hasNextIteratorResult = null;
  }

  /**
   * Retrieves the next tile coordinates.
   *
   * @returns The coordinates of the next tile or undefined if no more tiles are available.
   * @throws Will throw an error if there are no more tiles available.
   */
  public next(): { x: number; y: number } {
    const iterator = this.hasNextIteratorResult ?? this.generator.next();
    this.hasNextIteratorResult = null;
    if (iterator.done) {
      throw new Error("No more tiles available");
    }
    return iterator.value;
  }

  /**
   * Checks if there are more tiles to process.
   *
   * @returns True if there are more tiles, false otherwise.
   */
  public hasNext(): boolean {
    this.hasNextIteratorResult =
      this.hasNextIteratorResult ?? this.generator.next();
    return this.hasNextIteratorResult.done === false;
  }

  /**
   * Generator function that yields the coordinates of each tile in the canvas.
   *
   * @returns A generator yielding objects with x and y properties for each tile position.
   * @yields {{ x: number; y: number }} The coordinates of the next tile in the canvas.
   */
  private *createGenerator(): Generator<{ x: number; y: number }> {
    for (let y = 0; y < this.canvasSize.height; y += this.tilesSize) {
      for (let x = 0; x < this.canvasSize.width; x += this.tilesSize) {
        yield { x, y };
      }
    }
  }
}
